import React, { lazy, Suspense } from 'react';
import HeaderContext from './contexts/HeaderContext';
import { routes } from './routes';
import { Route, Switch, withRouter } from 'react-router';
import AppSpinner from './AppSpinner';

function lazyRoute({ modulePromise, loader }) {
	return lazy(() => {
		if (loader) {
			return loader();
		}
		return modulePromise().then((module) => {
			const Component = module.default;
			return {
				default: withRouter((routeData) => (
					<HeaderContext.Consumer>
						{({ setRenderHeaderState }) => {
							const matchedRoute = routes.find((route) => `${route.path}` === routeData.match.path);
							const shouldRenderHeader = (matchedRoute && matchedRoute.header) || false;
							setRenderHeaderState(shouldRenderHeader);

							return <Component routeData={routeData} />;
						}}
					</HeaderContext.Consumer>
				)),
			};
		});
	});
}

export function lazyIndexRoute() {
	return lazyRoute({
		loader: () => {
			return Promise.all([
				import(/* webpackChunkName: "AppContainer" */ './containers'),
				import(/* webpackChunkName: "Header" */ './components/common/Header'),
			]).then((modules) => {
				const Wrapper = modules[0].default;
				const Header = modules[1].default;
				return {
					default: withRouter((routeData) => (
						<Wrapper routeData={routeData} header={<Header />}>
							<Suspense fallback={<AppSpinner />}>
								<Switch>
									{routes.map(({ component, path, exact }) => {
										return (
											<Route
												exact={exact || false}
												path={path}
												key={path}
												component={component()}
											/>
										);
									})}
								</Switch>
							</Suspense>
						</Wrapper>
					)),
				};
			});
		},
	});
}

export function lazyQuestionAskPage() {
	return lazyRoute({
		modulePromise: () =>
			import(
				/* webpackChunkName: "QuestionAskPage" */ './containers/QuestionAskPage/QuestionAskPage'
			),
	});
}

export function lazyQuestionEditPage() {
	return lazyRoute({
		modulePromise: () =>
			import(
				/* webpackChunkName: "QuestionEditPage" */ './containers/QuestionEditPage/QuestionEditPage'
			),
	});
}

export function lazyQuestionViewPage() {
	return lazyRoute({
		modulePromise: () =>
			import(
				/* webpackChunkName: "QuestionViewPage" */ './containers/QuestionViewPage/QuestionViewPage'
			),
	});
}

export function lazyAdminPage() {
	return lazyRoute({
		modulePromise: () =>
			import(/* webpackChunkName: "TopicCreatePage" */ './containers/AdminPage/AdminPage'),
	});
}

export function lazyTopicCreatePage() {
	return lazyRoute({
		modulePromise: () =>
			import(
				/* webpackChunkName: "TopicCreatePage" */ './containers/TopicCreatePage/TopicCreatePage'
			),
	});
}

export function lazyTopicEditPage() {
	return lazyRoute({
		modulePromise: () =>
			import(/* webpackChunkName: "TopicEditPage" */ './containers/TopicEditPage/TopicEditPage'),
	});
}

export function lazyExpertListPage() {
	return lazyRoute({
		modulePromise: () =>
			import(/* webpackChunkName: "ExpertListPage" */ './containers/ExpertListPage/ExpertListPage'),
	});
}

export function lazyQuestionListPage() {
	return lazyRoute({
		modulePromise: () =>
			import(
				/* webpackChunkName: "QuestionListPage" */ './containers/QuestionListPage/QuestionsListPage'
			),
	});
}

export function lazyQuestionListMacroPage() {
	return lazyRoute({
		modulePromise: () =>
			import(
				/* webpackChunkName: "QuestionsListMacroPage" */ './containers/QuestionListMacroPage/QuestionsListMacroPage'
			),
	});
}

export function lazyQuestionListByTopicPage() {
	return lazyRoute({
		modulePromise: () =>
			import(
				/* webpackChunkName: "QuestionListByTopicPage" */ './containers/QuestionListByTopicPage/QuestionListByTopicPage'
			),
	});
}

export function lazyTopicListPage() {
	return lazyRoute({
		modulePromise: () =>
			import(/* webpackChunkName: "TopicListPage" */ './containers/TopicListPage/TopicListPage'),
	});
}

export function lazyActionPage() {
	return lazyRoute({
		modulePromise: () =>
			import(/* webpackChunkName: "ActionPage" */ './containers/ActionPage/ActionPage'),
	});
}
